'use client'

import { useState, type CSSProperties } from 'react'

import Link from 'next/link'
import { PoweredByPaddle } from '@/components/PoweredByPaddle'
import { LegalHero, RefPlus } from '@/components/reference/misc/kit'

export default function TermsOfServicePage() {
  const [openSections, setOpenSections] = useState<number[]>([0]) // First section open by default

  const toggleSection = (index: number) => {
    setOpenSections(prev =>
      prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index]
    )
  }

  const sections = [
    {
      title: 'Agreement to Terms',
      content: (
        <>
          <p>
            Welcome to Pixelmojo (&quot;Company,&quot; &quot;we,&quot;
            &quot;us,&quot; or &quot;our&quot;). These Terms of Service
            (&quot;Terms&quot;) govern your access to and use of our website
            located at{' '}
            <a href='https://www.pixelmojo.io' className='lg-a'>
              pixelmojo.io
            </a>{' '}
            (the &quot;Site&quot;) and all related services (collectively, the
            &quot;Services&quot;).
          </p>
          <p>
            By accessing or using our Services, you agree to be bound by these
            Terms. If you disagree with any part of these Terms, you may not
            access our Services.
          </p>
          <div className='lg-note lg-note--warn'>
            <p className='lg-strong lg-iconrow'>Important Notice</p>
            <p className='lg-sm'>
              These Terms contain an arbitration clause and class action waiver
              that affects your legal rights. Please read Section on Dispute
              Resolution carefully.
            </p>
          </div>
        </>
      ),
    },
    {
      title: 'Services & Deliverables',
      content: (
        <>
          <h3 className='lg-h'>
            <span className='lg-accent'>→</span> Scope of Services
          </h3>
          <p>
            The specific Services to be provided will be detailed in a Statement
            of Work (SOW) or project agreement. Services may include:
          </p>
          <div className='lg-grid'>
            {[
              'AI Visibility Strategy',
              'AI Product Development',
              'Brand & Sales Design',
              'Growth Marketing',
            ].map((service, i) => (
              <div key={i} className='lg-cell lg-row lg-row--center'>
                <span className='lg-num'>{i + 1}</span>
                <span className='lg-medium'>{service}</span>
              </div>
            ))}
          </div>

          <h3 className='lg-h'>
            <span className='lg-accent'>→</span> Client Responsibilities
          </h3>
          <p>To ensure successful project delivery, the Client agrees to:</p>
          <div className='lg-stack'>
            {[
              {
                title: 'Timely Feedback',
                desc: 'Provide feedback and approvals within 3-5 business days',
              },
              {
                title: 'Materials & Access',
                desc: 'Supply all necessary materials, content, and credentials',
              },
              {
                title: 'Single Point of Contact',
                desc: 'Designate one person for project communication',
              },
              {
                title: 'Meeting Participation',
                desc: 'Attend scheduled meetings and reviews',
              },
              {
                title: 'Payment Schedule',
                desc: 'Pay invoices according to agreed terms',
              },
            ].map((item, i) => {
              return (
                <div key={i} className='lg-cell lg-row'>
                  <div className='lg-tile'></div>
                  <div>
                    <p className='lg-strong'>{item.title}</p>
                    <p className='lg-sm lg-muted'>{item.desc}</p>
                  </div>
                </div>
              )
            })}
          </div>

          <h3 className='lg-h'>
            <span className='lg-accent'>→</span> Project Timelines & Revisions
          </h3>
          <div className='lg-grid'>
            <div className='lg-cell'>
              <p className='lg-strong'>⏱️ Timelines</p>
              <p className='lg-sm lg-muted'>
                Project timelines are estimates and may adjust based on client
                feedback cycles or scope changes. Delays caused by late feedback
                may extend timelines.
              </p>
            </div>
            <div className='lg-cell'>
              <p className='lg-strong'>✏️ Revisions</p>
              <p className='lg-sm lg-muted'>
                Revision rounds specified in SOW. Additional revisions beyond
                agreed scope may incur extra charges at standard hourly rate.
              </p>
            </div>
          </div>
        </>
      ),
    },
    {
      title: 'Radar Platform',
      content: (
        <>
          <h3 className='lg-h'>
            <span className='lg-accent'>→</span> Radar Credits and Runs
          </h3>
          <p>
            Radar is sold as one-time audit packs and a monthly Pro retainer.
            One pack credit equals one audit run and is consumed when a run is
            initiated; pack credits do not expire unless otherwise stated at
            purchase. Pro retainer audits are a monthly allowance that resets
            each billing cycle and does not roll over. Single-tool runs
            purchased individually are valid for one run of the purchased tool.
            Partially completed runs due to user action (e.g., closing the
            browser) are still counted as used.
          </p>
          <div className='lg-grid'>
            <div className='lg-note'>
              <p className='lg-strong'>Free Tier</p>
              <p className='lg-sm lg-muted'>
                One free tool test per email address, verified by email code. A
                selection of technical readiness tools is included; the current
                lineup is shown on the{' '}
                <a href='/tools' className='lg-a'>
                  /tools
                </a>{' '}
                page and may change.
              </p>
            </div>
            <div className='lg-note'>
              <p className='lg-strong'>Credit Packs</p>
              <p className='lg-sm lg-muted'>
                Purchased via Paddle. See{' '}
                <a href='/refund-policy' className='lg-a'>
                  Refund Policy
                </a>{' '}
                for details.
              </p>
            </div>
          </div>

          <h3 className='lg-h'>
            <span className='lg-accent'>→</span> Accuracy Disclaimer
          </h3>
          <p>
            Radar queries live AI systems (ChatGPT, Claude, Gemini, Perplexity)
            whose outputs change over time. Results reflect the state of those
            systems at the time of the audit and are not guaranteed to be
            accurate, complete, or permanent. Radar output is informational and
            diagnostic. It does not guarantee AI citations, search rankings, AI
            or search visibility, or any commercial outcome. Pixelmojo is not
            liable for business decisions made based on Radar output.
          </p>

          <h3 className='lg-h'>
            <span className='lg-accent'>→</span> Acceptable Use
          </h3>
          <p>You may not use Radar to:</p>
          <div className='lg-stack'>
            {[
              'Run automated bulk audits of domains you do not own or have permission to audit',
              'Resell raw audit output without attribution to Pixelmojo',
              'Attempt to reverse-engineer or abuse the underlying AI API connections',
              'Circumvent rate limits, token validation, or credit consumption mechanisms',
            ].map((item, i) => (
              <div key={i} className='lg-cell lg-cell--sm lg-row'>
                <span className='lg-accent lg-strong'>
                  {String.fromCharCode(97 + i)})
                </span>
                <span className='lg-sm'>{item}</span>
              </div>
            ))}
          </div>

          <h3 className='lg-h'>
            <span className='lg-accent'>→</span> Third-Party Service
            Availability
          </h3>
          <p>
            Radar&apos;s functionality depends on third-party AI APIs. Pixelmojo
            is not liable for disruptions caused by downtime or policy changes
            at OpenAI, Anthropic, Google (including Google Search Console),
            Perplexity, Reddit, or YouTube.
          </p>

          <h3 className='lg-h'>
            <span className='lg-accent'>→</span> Merchant of Record
          </h3>
          <p>
            All Radar payments are processed by{' '}
            <a
              href='https://paddle.com'
              target='_blank'
              rel='noopener noreferrer'
              className='lg-a'
            >
              Paddle.com
            </a>{' '}
            as Merchant of Record. Paddle&apos;s{' '}
            <a
              href='https://www.paddle.com/legal/terms'
              target='_blank'
              rel='noopener noreferrer'
              className='lg-a'
            >
              terms
            </a>{' '}
            and{' '}
            <a
              href='https://www.paddle.com/legal/privacy'
              target='_blank'
              rel='noopener noreferrer'
              className='lg-a'
            >
              privacy policy
            </a>{' '}
            apply to all transactions.
          </p>
          <div>
            <PoweredByPaddle variant='card' className='lg-paddle' />
          </div>
        </>
      ),
    },
    {
      title: 'Public Knowledge API and MCP Server',
      content: (
        <>
          <p>
            Our public Ask API and MCP integration provide AI-generated answers
            about Pixelmojo using published material. Answers may be incomplete,
            inaccurate, or out of date. Check the cited source pages for current
            product, pricing, and access details; an answer does not place an
            order or change the terms of a purchase or project agreement.
          </p>
          <p>
            These knowledge services do not require an account and cannot read
            private customer records, take payments, create or delete accounts,
            or change your data. Ask executions are limited per client IP,
            currently to 10 per minute and 50 per day across REST and MCP. Data
            processing is described in our{' '}
            <Link href='/privacy-policy' className='lg-a'>
              Privacy Policy
            </Link>
            .
          </p>
        </>
      ),
    },
    {
      title: 'Pricing & Payment',
      content: (
        <>
          <h3 className='lg-h'>Pricing Models</h3>
          <div className='lg-stack'>
            {[
              {
                model: '60-Day Conversion Sprint',
                price: '₱180K (PH) / $4,500 (Global)',
                terms: '50% upfront, 50% at day 30',
                color: 'primary',
              },
              {
                model: 'Growth Retainer',
                price: '$3,500/month base + performance bonuses',
                terms: 'Monthly billing at start of month',
                color: 'secondary',
              },
              {
                model: 'Project-Based',
                price: 'Fixed fee for defined scope',
                terms: 'Typically 50% upfront, 50% upon completion',
                color: 'growth',
              },
            ].map((pricing, i) => (
              <div key={i} className={`lg-note lg-note--${pricing.color}`}>
                <div className='lg-split'>
                  <div>
                    <h4 className='lg-strong'>{pricing.model}</h4>
                    <p className='lg-sm lg-muted'>{pricing.price}</p>
                    <p className='lg-xs lg-muted lg-iconrow'>{pricing.terms}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <h3 className='lg-h'>
            <span className='lg-accent'>→</span> Payment Terms
          </h3>
          <div className='lg-cell'>
            <ul className='lg-stack'>
              {[
                { label: 'Invoice Due', value: '7 days from issuance' },
                {
                  label: 'Late Payment Fee',
                  value: '2% monthly interest charge',
                },
                {
                  label: 'Service Suspension',
                  value: 'May occur for non-payment',
                },
                {
                  label: 'Refund Policy',
                  value:
                    'Professional-services fees are non-refundable once work commences. Radar purchases are covered by our separate Refund Policy.',
                },
                {
                  label: 'Scope Changes',
                  value:
                    'Documented via Change Order, may incur additional fees',
                },
              ].map((item, i) => (
                <li key={i} className='lg-split lg-split--rule'>
                  <span className='lg-sm lg-muted'>{item.label}</span>
                  <span className='lg-sm lg-strong'>{item.value}</span>
                </li>
              ))}
            </ul>
          </div>
        </>
      ),
    },
    {
      title: 'Intellectual Property Rights',
      content: (
        <>
          <h3 className='lg-h'>
            <span className='lg-accent'>→</span> Ownership of Deliverables
          </h3>
          <p>
            Upon <strong>full payment</strong> of all fees, Client will own all
            rights to the final Deliverables created specifically for the Client
            under the project agreement.
          </p>
          <div className='lg-grid'>
            {[
              'Custom designs and visual assets',
              'Custom-developed code and applications',
              'Brand assets and identity systems',
              'Content created specifically for Client',
            ].map((item, i) => (
              <div
                key={i}
                className='lg-cell lg-cell--sm lg-row lg-row--tight lg-row--center'
              >
                <span className='lg-sm'>{item}</span>
              </div>
            ))}
          </div>

          <h3 className='lg-h'>
            <span className='lg-accent'>→</span> Pixelmojo Retains
          </h3>
          <div className='lg-cell lg-stack'>
            {[
              'Proprietary methodologies, frameworks, and processes',
              'Code libraries, templates, and reusable components',
              'Design systems and pattern libraries',
              'AI models, prompts, and training data',
              'Tools and automation workflows',
            ].map((item, i) => (
              <div key={i} className='lg-row lg-row--tight'>
                <span className='lg-accent'>•</span>
                <span className='lg-sm lg-muted'>{item}</span>
              </div>
            ))}
          </div>

          <div className='lg-grid'>
            <div className='lg-note lg-note--warn'>
              <p className='lg-strong'>Third-Party Assets</p>
              <p className='lg-sm'>
                Some deliverables may include third-party assets (fonts,
                plugins, APIs) that are licensed. Client responsible for
                maintaining valid licenses.
              </p>
            </div>
            <div className='lg-note'>
              <p className='lg-strong'>Portfolio Rights</p>
              <p className='lg-sm lg-muted'>
                Pixelmojo retains right to display work in portfolio unless
                otherwise agreed. We respect NDAs and can anonymize sensitive
                projects.
              </p>
            </div>
          </div>
        </>
      ),
    },
    {
      title: 'Warranties & Disclaimers',
      content: (
        <>
          <h3 className='lg-h'>Our Warranties</h3>
          <div className='lg-stack'>
            {[
              'Services performed in professional and workmanlike manner',
              'Deliverables conform to agreed specifications',
              'We have right to provide Services and grant licenses',
              'Our work will not infringe third-party IP rights',
            ].map((warranty, i) => (
              <div key={i} className='lg-cell lg-row'>
                <span className='lg-sm'>{warranty}</span>
              </div>
            ))}
          </div>

          <div className='lg-note lg-note--ink'>
            <p className='lg-label lg-label--ink'>Disclaimer</p>
            <p className='lg-sm'>
              <strong>
                EXCEPT AS EXPRESSLY PROVIDED, OUR SERVICES ARE PROVIDED &quot;AS
                IS&quot; WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED.
              </strong>
            </p>
            <p className='lg-sm'>We do not warrant that:</p>
            <ul className='lg-stack lg-stack--xs lg-sm'>
              {[
                'Services will be uninterrupted or error-free',
                'Results will meet your specific business objectives',
                'All errors or defects will be corrected',
                'Services will achieve specific conversion rates or revenue targets',
              ].map((item, i) => (
                <li key={i} className='lg-row lg-row--tight'>
                  <span>•</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <h3 className='lg-h'>Limitation of Liability</h3>
          <div className='lg-note lg-note--danger'>
            <p className='lg-sm lg-strong'>
              To the maximum extent permitted by law, Pixelmojo shall not be
              liable for any indirect, incidental, special, consequential, or
              punitive damages.
            </p>
            <p className='lg-sm'>
              Our total liability shall not exceed the amount paid by Client to
              Pixelmojo in the 12 months preceding the claim.
            </p>
          </div>
        </>
      ),
    },
    {
      title: 'Termination & Dispute Resolution',
      content: (
        <>
          <h3 className='lg-h'>
            <span className='lg-accent'>→</span> Termination Terms
          </h3>
          <div className='lg-grid'>
            <div className='lg-cell'>
              <p className='lg-strong'>By Client</p>
              <p className='lg-sm lg-muted'>14 days written notice required</p>
              <p className='lg-sm lg-muted'>
                <strong>Client responsible for:</strong>
              </p>
              <ul className='lg-stack lg-stack--xs lg-sm lg-muted'>
                <li>• All fees for work completed</li>
                <li>• 50% of remaining project fees</li>
                <li>• Third-party costs incurred</li>
              </ul>
            </div>
            <div className='lg-cell'>
              <p className='lg-strong'>By Pixelmojo</p>
              <p className='lg-sm lg-muted'>Immediate termination if Client:</p>
              <ul className='lg-stack lg-stack--xs lg-sm lg-muted'>
                <li>• Fails to pay within 30 days</li>
                <li>• Breaches these Terms</li>
                <li>• Engages in abusive conduct</li>
                <li>• Provides false information</li>
              </ul>
            </div>
          </div>

          <h3 className='lg-h'>
            <span className='lg-accent'>→</span> Dispute Resolution
          </h3>
          <div className='lg-stack'>
            <div className='lg-note'>
              <p className='lg-strong'>1. Informal Resolution</p>
              <p className='lg-sm lg-muted'>
                Contact us at{' '}
                <a href='mailto:founders@pixelmojo.io' className='lg-a'>
                  founders@pixelmojo.io
                </a>{' '}
                to attempt informal resolution before filing formal dispute.
              </p>
            </div>
            <div className='lg-note lg-note--blue'>
              <p className='lg-strong'>2. Binding Arbitration</p>
              <p className='lg-sm lg-muted'>
                Disputes resolved through arbitration in accordance with
                Philippine Arbitration Law, conducted in Metro Manila,
                Philippines.
              </p>
            </div>
            <div className='lg-note lg-note--warn'>
              <p className='lg-strong lg-iconrow'>Class Action Waiver</p>
              <p className='lg-sm'>
                <strong>
                  YOU AGREE THAT DISPUTES WILL BE RESOLVED INDIVIDUALLY, NOT AS
                  PART OF A CLASS ACTION OR REPRESENTATIVE PROCEEDING.
                </strong>
              </p>
            </div>
          </div>

          <h3 className='lg-h'>
            <span className='lg-accent'>→</span> Governing Law
          </h3>
          <div className='lg-cell lg-row'>
            <div>
              <p className='lg-sm lg-muted'>
                These Terms are governed by the{' '}
                <strong>laws of the Republic of the Philippines</strong>,
                without regard to conflict of law principles. Any legal action
                must be brought in the courts of{' '}
                <strong>Makati City, Metro Manila, Philippines</strong>.
              </p>
            </div>
          </div>
        </>
      ),
    },
  ]

  return (
    <div className='shell pg-blog pg-legal'>
      <section className='sheet bhero bpage' id='top' data-rail='Terms'>
        <div className='container'>
          <LegalHero
            eyebrow='Legal'
            title='Terms of Service'
            description='Terms and conditions for using our website and services. Last updated: September 24, 2026'
            ctaText='Contact Us'
            ctaHref='/contact-us'
          />

          <div className='barticle__grid'>
            {/* Key Highlights */}
            <aside className='bside'>
              <div className='lg-toc'>
                <h2 className='lg-toc__title'>Key Highlights</h2>
                <div className='lg-facts'>
                  {[
                    {
                      label: 'Clear Pricing',
                      desc: 'Fixed pricing with no hidden fees',
                    },
                    {
                      label: 'IP Protection',
                      desc: 'You own deliverables after payment',
                    },
                    {
                      label: 'Flexible Terms',
                      desc: '60-day sprints or monthly retainers',
                    },
                  ].map((item, i) => {
                    return (
                      <div key={i} className='lg-fact'>
                        <p>{item.label}</p>
                        <p>{item.desc}</p>
                      </div>
                    )
                  })}
                </div>
              </div>
            </aside>

            {/* Accordion Sections */}
            <article className='post'>
              <div className='lg-secs'>
                {sections.map((section, index) => {
                  const isOpen = openSections.includes(index)

                  return (
                    <div key={index} className='lg-sec' data-open={isOpen}>
                      <button
                        type='button'
                        onClick={() => toggleSection(index)}
                        aria-controls={`terms-section-${index}`}
                        aria-expanded={isOpen}
                        className='lg-sec__head'
                      >
                        <div className='lg-sec__title'>
                          <span className='lg-sec__n'>Section {index + 1}</span>
                          <h3>{section.title}</h3>
                        </div>
                        <span className='lg-plus' aria-hidden='true'>
                          <RefPlus />
                        </span>
                      </button>

                      <div
                        id={`terms-section-${index}`}
                        className='lg-sec__body'
                        inert={!isOpen}
                      >
                        <div className='lg-sec__clip'>
                          <div className='lg-body'>{section.content}</div>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </article>
          </div>
        </div>
      </section>

      <section
        className='sheet sheet--pad bafter bpage lg-after sheet--last'
        data-rail='Contact'
        style={{ '--bg': 'var(--paper-2)' } as CSSProperties}
      >
        <div className='container'>
          {/* Contact Section */}
          <div className='lg-contact'>
            <div className='lg-contact__head'>
              <h2>Questions About Terms?</h2>
              <p>
                Our team is here to clarify any questions about these terms.
              </p>
            </div>
            <div className='lg-contact__cells'>
              <div className='lg-contact__cell'>
                <p className='lg-label'>General Inquiries</p>
                <a href='mailto:founders@pixelmojo.io' className='lg-biglink'>
                  founders@pixelmojo.io
                </a>
              </div>
              <div className='lg-contact__cell'>
                <p className='lg-label'>Legal Matters</p>
                <a href='mailto:legal@pixelmojo.io' className='lg-biglink'>
                  legal@pixelmojo.io
                </a>
              </div>
              <div className='lg-contact__cell lg-contact__cell--wide'>
                <p className='lg-label'>Physical Address</p>
                <p>
                  111 Paseo de Roxas, Legazpi Village, Makati, 1229 Metro
                  Manila, Philippines
                </p>
              </div>
            </div>
          </div>

          {/* Related Links */}
          <div className='lg-related'>
            <p>Related policies</p>
            <div className='hero__cta'>
              <Link href='/privacy-policy' className='btn'>
                Privacy Policy →
              </Link>
              <Link href='/refund-policy' className='btn btn--ghost'>
                Refund Policy →
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
